// RecordDetailInline — right panel of master-detail Records view
// Shows record details inline (NOT as a separate page)
// Layout matches the HTML prototype exactly (compact header, star rating, 2fr:3fr grid)

import { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRecordDetail } from '@/hooks/records/useRecordDetail';
import { SpeakerTalkRatio } from '@/components/details/SpeakerTalkRatio';
import { AudioPlayer, AudioPlayerSkeleton } from '@/components/details/AudioPlayer';
import { TranscriptViewer } from '@/components/details/TranscriptViewer';
import { AnalysisPanel } from '@/components/details/v2/AnalysisPanel';
import { DemoAnalysisNotice } from '@/features/demo/DemoAnalysisNotice';
import { AnnotationModal } from '@/components/modals/AnnotationModal';
import { IncidentModal } from '@/components/modals/IncidentModal';
import { AgentReviewSection } from '@/components/details/analysis/AgentReviewSection';
import { ShortCallBanner } from '@/components/data';
import { RecordTimestamps } from '@/components/records/RecordTimestamps';
import { SaleProbabilityBadge } from '@/components/records/SaleProbabilityBadge';
import { RecordHaltedNotice } from '@/components/records/RecordHaltedNotice';
import { RecordFailedNotice } from '@/components/records/RecordFailedNotice';
import {
  isFailedRecordStatus,
  isHaltedRecordStatus,
  isReanalyzeableRecordStatus,
  isRestartPendingRecord,
} from '@/lib/records/status';
import { SkeletonCard, SkeletonMedia } from '@/components/ui/skeleton';
import { AnimatedContainer } from '@/components/transitions';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import {
  DEFAULT_MIN_CALL_DURATION_SECONDS,
  hidesHesitationSounds,
  renderTranscriptText,
  transcriptDisplayOptions,
} from '@aica/core';
import {
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Phone,
  PhoneIncoming,
  PhoneOutgoing,
  MessageSquare,
  X,
  AlertCircle,
  PhoneMissed,
  Copy,
  Plus,
  AlertTriangle,
  StickyNote,
  RefreshCw,
  Download,
  Link2,
  Pencil,
  Trash2,
  Sparkles,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatQualityScore } from '@/lib/format-quality-score';
import { speakerLabel, speakerNamesFor } from '@/lib/transcript-speaker';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { logError } from '@/lib/logger';
import { CrmEntityLink } from '@/components/data/CrmEntityLink';
import { AssistantEntityChip } from '@/components/assistant/AssistantEntityChip';
import { useAssistantDrawerSafe } from '@/contexts/AssistantDrawerContext';
import {
  useAssistantHostRegistry,
  type AssistantHostEntity,
} from '@/contexts/AssistantHostContext';
import {
  assistantHostKind,
  useAssistantHostBridge,
} from '@/hooks/assistant/useAssistantHostBridge';
import type { Annotation } from '@/components/details/TranscriptViewer';
import type { CrmEntityType } from '@/lib/utils/crm-links';
import { Link } from '@tanstack/react-router';
import { cn, formatDateLocale, formatDateTimeLocaleShort } from '@/lib/utils';
import { formatDurationUnit } from '@/lib/format-duration';
import { getCategoryTagStyle } from '@/lib/categoryBadgeStyle';
import { callCategoryLabelKey } from '@/lib/records/call-category-label';
import { resolveStageBadgeData } from '@/lib/records/resolve-stage-badge';
import { DealOutcomeBadge } from '@/components/records/DealOutcomeBadge';
import {
  participantsPlainLabel,
  participantsDetailedLabel,
} from '@/components/records/participants-label';

export interface RecordDetailInlineProps {
  recordId: string;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  onSeriesClick?: (seriesId: string) => void;
  /** Override apiClient for embedded mode */
  apiClient?: ReturnType<typeof useAuth>['apiClient'];
  /** Override selectedOrgId for embedded mode */
  selectedOrgId?: string | null;
  /**
   * The org the caller selected `recordId` under. When given, the detail
   * query stays disabled unless it still matches the live org — an
   * independent backstop against the caller's own render gate. See
   * useRecordDetail's expectedOrgId doc for why this is a second, separate
   * gate rather than reusing `selectedOrgId` above.
   */
  expectedOrgId?: string | null;
  /** Connected Bitrix24 domain for CRM actions */
  bitrix24Domain?: string | null;
  /** User role — agents cannot reanalyze or create alerts */
  userRole?: string | null;
  /** Optional evidence clip intent from an alert deep link. */
  segmentStartSeconds?: number;
  /**
   * Which opening of the record carried `segmentStartSeconds`. A host that can
   * open the same record at the same moment again (the CRM tab) passes a new
   * value per opening, so the player goes back to the moment (#2966); without
   * it, an opening is told apart by the record and its moment alone.
   */
  openKey?: number;
  segmentDurationSeconds?: number;
  alertId?: string;
  /** A client quote to mark in the transcript - the objection the reader came from (#846). */
  highlightQuote?: string;
  /**
   * Where a citation of ANOTHER record opens, when the caller has a place for
   * it (the CRM card's tab, #745). Handed to the assistant host; without it the
   * assistant deep-links to the dashboard.
   */
  onOpenAssistantRecord?: (recordId: string, atSec?: number) => void;
  /**
   * Право запускать анализ вручную и обработчик запуска ЭТОЙ записи —
   * пришедшие от `ProcessingState` (портал: `useProcessingState`, встроенный
   * режим: `useEmbeddedProcessingState`). Пропсы, а не собственный вызов
   * хука внутри компонента: `useProcessingState` читает `UnifiedAuthContext`
   * (Clerk), которого под `/embedded/*` нет — тем же способом, каким этот
   * компонент уже принимает `apiClient`/`selectedOrgId` для встроенного
   * режима. Просто прокидываются в `RecordHaltedNotice`; без них (как без
   * `apiClient`) кнопка запуска не рендерится.
   */
  canStart?: boolean;
  onStart?: () => void;
  isStarting?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getScoreStyle(score: number): string {
  if (score >= 70) return 'bg-success-dim text-success-fg';
  if (score >= 50) return 'bg-warning-dim text-warning-fg';
  return 'bg-danger-dim text-danger-fg';
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * How much shorter the conversation must be before the panel shows it beside
 * the recording length.
 *
 * Nearly every call has a second or two of lead-in, and printing "0:45 ·
 * conversation 0:44" on all of them is noise. The number is surfaced when the
 * gap is large enough that a customer looking at the charge would otherwise
 * wonder what they paid for — which is the same case the billing change cares
 * about (ringing, an IVR greeting, hold music).
 */
const SPEECH_GAP_VISIBLE_SECONDS = 5;

// ---------------------------------------------------------------------------
// AgentRating — interactive star rating for agent's feedback on AI accuracy
// ---------------------------------------------------------------------------

interface AgentRatingProps {
  recordId: string;
  initialRating: number | null | undefined;
  initialFeedback: string | null | undefined;
  raterName?: string | null;
  apiClient: ReturnType<typeof useAuth>['apiClient'];
  size?: 'sm' | 'md';
}

function AgentRating({
  recordId,
  initialRating,
  initialFeedback,
  raterName,
  apiClient,
  size = 'md',
}: AgentRatingProps) {
  const { t } = useTranslation(['records', 'common']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rating, setRating] = useState<number | null>(initialRating ?? null);
  const [hovered, setHovered] = useState<number | null>(null);
  const [feedback, setFeedback] = useState(initialFeedback ?? '');
  const [saving, setSaving] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // Reset state when switching to a different record
  useEffect(() => {
    setRating(initialRating ?? null);
    setFeedback(initialFeedback ?? '');
    setFeedbackOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId]);

  const handleRate = async (star: number) => {
    const newRating = rating === star ? null : star;
    const prevRating = rating;
    setRating(newRating);
    if (newRating != null) setFeedbackOpen(true);
    setSaving(true);
    try {
      await apiClient.records.updateAgentRating(recordId, newRating, feedback || null);
      void queryClient.invalidateQueries({ queryKey: ['records', 'detail', recordId] });
      void queryClient.invalidateQueries({ queryKey: ['records', 'infinite'] });
    } catch {
      setRating(prevRating);
      toast.error(t('records.rating.saveFailed', 'Failed to save rating'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFeedback = async () => {
    if (rating == null) return;
    setSaving(true);
    try {
      await apiClient.records.updateAgentRating(recordId, rating, feedback || null);
      setFeedbackOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['records', 'detail', recordId] });
      void queryClient.invalidateQueries({ queryKey: ['records', 'infinite'] });
    } catch {
      toast.error(t('records.rating.saveFailed', 'Failed to save rating'));
    } finally {
      setSaving(false);
    }
  };

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (feedbackOpen && textareaRef.current) {
      const el = textareaRef.current;
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
      el.focus();
      el.selectionStart = el.value.length;
    }
  }, [feedbackOpen]);

  const handleFeedbackInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFeedback(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  const displayRating = hovered ?? rating ?? 0;
  const starClass = size === 'sm' ? 'text-sm' : 'text-lg';
  const starPadding = size === 'sm' ? '0px 2px' : '1px 3px';
  const feedbackTooLong = feedback.length > 1000;

  return (
    <div className="relative flex flex-col gap-1 min-w-0">
      <div className="flex items-center gap-0.5">
        <div
          className="flex"
          style={{ gap: '2px' }}
          title={t('records.rating.rateAnalysisAccuracy', 'Rate analysis accuracy')}
        >
          {[1, 2, 3, 4, 5].map((i) => (
            <button
              key={i}
              type="button"
              disabled={saving}
              className={`p-0 leading-none ${starClass} bg-transparent border-none transition-colors`}
              style={{ padding: starPadding, cursor: saving ? 'wait' : 'pointer' }}
              onClick={() => void handleRate(i)}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            >
              <span className={i <= displayRating ? 'text-warning' : 'text-fg-faint'}>
                {'\u2605'}
              </span>
            </button>
          ))}
        </div>
        {/* Name what the stars mean, with the exact wording the records filter
            uses. Unlabelled next to an agent's name they read as a rating OF
            the agent; they are the agent's verdict on the ANALYSIS accuracy. */}
        <span
          className={`ml-1 whitespace-nowrap text-fg-muted ${
            size === 'sm' ? 'text-micro' : 'text-xs'
          }`}
        >
          {t('records.agentRating')}
        </span>
        {rating != null && (
          <span
            className={`ml-1 tabular-nums text-fg-muted ${
              size === 'sm' ? 'text-micro' : 'text-xs'
            }`}
          >
            {rating}/5
          </span>
        )}
        {rating == null && size === 'md' && (
          <span className="text-xs text-fg-subtle ml-1">
            {t('records.rating.ratePrompt', 'Rate accuracy')}
          </span>
        )}
      </div>

      {/* Rated row: "Оцінено · Name · comment" */}
      {rating != null && (
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-micro text-fg-subtle shrink-0">
            {t('records.rating.rated', 'Rated')}
          </span>
          {raterName && (
            <>
              <span className="text-micro text-fg-faint shrink-0">·</span>
              <span
                className="text-micro text-fg-muted shrink-0 truncate max-w-[120px]"
                title={raterName}
              >
                {raterName}
              </span>
            </>
          )}
          {!feedbackOpen && feedback && (
            <>
              <span className="text-micro text-fg-faint shrink-0">·</span>
              <span className="relative group inline-flex min-w-0">
                <button
                  type="button"
                  onClick={() => setFeedbackOpen(true)}
                  className="text-micro text-fg-muted italic truncate max-w-[140px] underline decoration-dotted decoration-fg-muted/40 underline-offset-2 hover:text-fg transition-colors focus-visible:outline-none"
                >
                  {feedback.length > 20 ? `${feedback.slice(0, 20)}…` : feedback}
                </button>
                {feedback.length > 20 && !feedbackOpen && (
                  <div
                    className={[
                      'absolute top-full left-1/2 -translate-x-1/2 mt-2 z-50 pointer-events-none',
                      'opacity-0 -translate-y-1',
                      'group-hover:opacity-100 group-hover:translate-y-0',
                      'transition-all duration-200 ease-out',
                      'w-max max-w-[260px]',
                    ].join(' ')}
                  >
                    <div className="absolute -top-[5px] left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 z-10 bg-bg-surface border-l border-t border-border" />
                    <div className="relative bg-bg-surface border border-border rounded-card px-3 py-2.5 shadow-[0_8px_32px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)] whitespace-normal break-words">
                      <p className="text-[9px] font-semibold uppercase tracking-widest text-primary mb-1.5 leading-none">
                        {t('records.rating.commentLabel', 'Коментар')}
                      </p>
                      <p className="text-xs text-fg leading-relaxed">{feedback}</p>
                    </div>
                  </div>
                )}
              </span>
            </>
          )}
        </div>
      )}

      {/* Floating feedback editor popup */}
      {feedbackOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setFeedbackOpen(false)} />
          <div className="absolute top-full right-0 mt-2 z-50 w-[300px] animate-in fade-in-0 zoom-in-95 duration-150">
            <div className="absolute -top-[5px] right-6 w-2.5 h-2.5 rotate-45 z-10 bg-bg-surface border-l border-t border-border" />
            <div className="relative bg-bg-surface border border-border rounded-card p-3 shadow-[0_8px_32px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)]">
              <p className="text-[9px] font-semibold uppercase tracking-widest text-primary mb-2 leading-none">
                {t('records.rating.commentLabel', 'Коментар')}
              </p>
              <textarea
                ref={textareaRef}
                value={feedback}
                onChange={handleFeedbackInput}
                placeholder={t(
                  'records.rating.feedbackPlaceholder',
                  'Leave a comment... (optional)'
                )}
                rows={1}
                className="w-full resize-none overflow-hidden text-xs bg-bg-elevated border border-border rounded px-2 py-1.5 text-fg placeholder:text-fg-subtle focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring transition-colors leading-relaxed"
                style={{ minHeight: '32px', maxHeight: '200px' }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setFeedbackOpen(false);
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey))
                    void handleSaveFeedback();
                }}
              />
              <div className="flex items-center justify-between mt-2">
                <span
                  className={`text-[10px] ${feedbackTooLong ? 'text-danger' : 'text-fg-subtle'}`}
                >
                  {feedbackTooLong
                    ? t('records.rating.commentTooLong', 'Maximum 1000 characters')
                    : `${feedback.length}/1000`}
                </span>
                <div className="flex gap-1.5">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setFeedbackOpen(false)}
                    className="text-xs px-2"
                  >
                    ✕
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={saving || feedbackTooLong}
                    onClick={() => void handleSaveFeedback()}
                    className="text-xs px-2"
                  >
                    {t('records.rating.saveFeedback', 'Save')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RecordDetailInline({
  recordId,
  onNavigatePrev,
  onNavigateNext,
  hasPrev = false,
  hasNext = false,
  onSeriesClick,
  apiClient: apiClientProp,
  selectedOrgId: selectedOrgIdProp,
  expectedOrgId,
  bitrix24Domain: bitrix24DomainProp,
  userRole,
  segmentStartSeconds,
  openKey,
  highlightQuote,
  onOpenAssistantRecord,
  segmentDurationSeconds = 20,
  alertId,
  canStart,
  onStart,
  isStarting,
}: RecordDetailInlineProps) {
  const isAgent = userRole === 'agent';
  const canCreateIncident = !isAgent;
  const { t, i18n } = useTranslation(['records', 'common']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [creatingTask, setCreatingTask] = useState(false);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  const [editAnnotation, setEditAnnotation] = useState<Annotation | null>(null);

  const [activeTab, setActiveTab] = useState<'transcript' | 'notes' | 'score'>(
    'transcript'
  );

  // Track panel width — narrow layout stacks stars/Score tab on own row.
  // Callback ref re-runs when React swaps the outer node between the
  // loading and loaded states, so the ResizeObserver always follows the
  // currently mounted element instead of staying attached to a detached one.
  const [panelWidth, setPanelWidth] = useState(9999);
  const [headerNode, setHeaderNode] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!headerNode) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setPanelWidth(entry.contentRect.width);
    });
    ro.observe(headerNode);
    // Seed initial width immediately so first paint picks up the real size
    // without waiting for the first ResizeObserver callback.
    setPanelWidth(headerNode.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, [headerNode]);
  // Tabs mode: panels < 900px use Score tab; wider panels show 2 columns side-by-side
  const isTabsMode = panelWidth < 900;
  // Stars below title when panel < 900px
  const isStarsBelow = panelWidth < 900;
  // Action bar's own narrow/wide switch — deliberately NOT isTabsMode. isTabsMode's 900px
  // was sized for the score-tab/2-column body split, not for fitting 5 buttons on one line;
  // measured with real i18n strings in a real browser, the 5-button row needs each button up
  // to ~180px (ru "Переанализировать" + icon + padding) to never overflow — five of those
  // plus 4×6px gaps is ~924px, which the 900px boundary doesn't guarantee. 1010px clears that
  // with margin. Reusing isTabsMode here would also pull the unrelated 2-column analysis grid
  // and meta-chip layout (lines below) into a switch point that only the button row needs.
  const isActionBarNarrow = panelWidth < 1010;

  // Reset score tab when switching to wide mode (score tab not available there)
  useEffect(() => {
    if (panelWidth >= 900 && activeTab === 'score') {
      setActiveTab('transcript');
    }
  }, [panelWidth, activeTab]);

  // Compact player: auto-collapse on short screens, user can toggle
  const [isSmallScreen, setIsSmallScreen] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight < 800 : false
  );
  const [playerExpanded, setPlayerExpanded] = useState<boolean | null>(null); // null = auto

  useEffect(() => {
    const mq = window.matchMedia('(max-height: 799px)');
    const onChange = (e: MediaQueryListEvent) => setIsSmallScreen(e.matches);
    mq.addEventListener('change', onChange);
    setIsSmallScreen(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Effective state: user override > auto
  const playerCompact = playerExpanded === null ? isSmallScreen : !playerExpanded;

  // Reset tab when record changes
  useEffect(() => {
    setActiveTab('transcript');
  }, [recordId]);

  // Close the incident form on record navigation — the modal is mounted
  // unconditionally (isOpen just toggles its visibility) and carries no
  // key={recordId}, so without this a form left open while the user presses
  // Next keeps A's typed title/description on screen while IncidentModal's
  // own severity-resync effect quietly rewrites severity to B's default —
  // submitting then files a mismatched incident against the wrong record.
  useEffect(() => {
    setShowIncidentModal(false);
  }, [recordId]);

  const {
    apiClient,
    record,
    transcript,
    analysis,
    scriptAdherence,
    scriptAdherenceLoading,
    criteria,
    criteriaLoading,
    criteriaFailed,
    builtinSources,
    builtinPausedByAelo,
    annotations,
    annotationsState,
    annotationsWritable,
    loading,
    transcriptLoading,
    analysisLoading,
    accessDenied,
    isSeedRecord,
    seedQualityScore,
    error,
    audioUrl,
    audioRecordId,
    audioState,
    audioPlayerRef,
    currentTime,
    setCurrentTime,
    bitrix24Domain,
    // reanalyze
    showReanalyze,
    setShowReanalyze,
    selectedPromptProfile,
    setSelectedPromptProfile,
    promptProfiles,
    profilesLoading,
    crmStagesReference,
    reanalyzeStatus,
    reanalyzeError,
    reanalyzing,
    elapsedSeconds,
    // modals
    showAnnotationModal,
    setShowAnnotationModal,
    annotationSelectedText,
    annotationStartPos,
    annotationEndPos,

    exportingPdf,
    // handlers
    handleSeekAudio,
    handlePlayAudioSegment,
    handleAnnotationCreate,
    handleAnnotationSuccess,
    handleExportPdf,
    handleReanalyze,
    handleResumeHalted,
    isResumingHalted,
    handleRetryFailed,
    isRetryingFailed,
  } = useRecordDetail(recordId, true, {
    isEmbedded: !!apiClientProp,
    apiClient: apiClientProp,
    bitrix24Domain: bitrix24DomainProp ?? null,
    selectedOrgId: selectedOrgIdProp,
    expectedOrgId,
  });

  // AI assistant (#765, §8.2): the entry button and the bridge to this record's
  // player. Availability is the provider's call (the runtime flag in the
  // standalone app, the session's `features.assistant` in the iframe): no
  // context, no button (owner's review, 17.09).
  const assistantDrawer = useAssistantDrawerSafe();
  // A subject whose access may not ask about a record (an analyst's grant,
  // #1677) is not offered the button: every question would be a 403. The
  // provider decides it, as it decides availability.
  const canAskAssistant = !!assistantDrawer && !assistantDrawer.recordRefused;

  // Зеркалит серверный гейт: /reanalyze принимает только эти статусы
  // (см. isReanalyzeableRecordStatus). Без этого условия кнопка рисовалась
  // для awaiting_start/awaiting_funds под текстом «Проанализировать» —
  // рядом с ПРАВИЛЬНОЙ кнопкой запуска из RecordHaltedNotice — и слала
  // POST на /reanalyze, который сервер всегда отклонял 400.
  const canReanalyze = !isAgent && isReanalyzeableRecordStatus(record?.status);
  // Повтор доступен тому, кому его разрешит сервер: `viewerCanUpdate` —
  // тот же `record.update`, что проверяет маршрут повтора, посчитанный для
  // этой записи (супервизор проекта — да, супервизор вне проекта — нет).
  // Прежде гейтом был `canStart` (`organization.update`), и супервизор с
  // правом повтора читал «повторить может администратор» (#1264, Codex).
  //
  // And only with something to retry FROM — the server's answer
  // (`retryOutcome: 'no_source'`), not a copy of its rule: a copy here had
  // already drifted (a chat with only a preview, which the server refuses).
  const canRetryFailed = Boolean(record?.viewerCanUpdate);
  const hasRetrySource = record?.retryOutcome !== 'no_source';
  // Neither is known while the panel shows the list row: it has no `fileUri`
  // and no `viewerCanUpdate`, so «nothing to retry from» or «an administrator
  // can retry» would be a guess (code review on #1264).
  const retryAnswerKnown = !isSeedRecord;

  // A retry left this record waiting for a start (#1264): it already has a
  // transcript or an analysis from the earlier run, so the panels show those
  // and never reach their fallbacks — the start action and the explanation
  // stand once above the tabs, like the failure notice.
  const isRestartPending = isRestartPendingRecord(record);

  // The opening this panel shows, with or without a moment: the player applies
  // it once (#2966). Once it is spent, it is not handed to the player again —
  // the player is unmounted by the Notes and Score tabs, and a fresh one would
  // otherwise put the listener back at the link's moment; it resumes at the
  // listener's place instead. Nor is it handed to the previous record's audio,
  // still in the player for a render after a record switch.
  const linkMomentKey = [
    recordId,
    openKey ?? '',
    alertId ?? '',
    segmentStartSeconds ?? '',
    segmentDurationSeconds,
  ].join(':');
  const [spentLinkMomentKey, setSpentLinkMomentKey] = useState<string | null>(null);
  const linkMomentPending =
    linkMomentKey !== spentLinkMomentKey && audioRecordId === recordId;
  const linkMoment = useMemo(
    () =>
      linkMomentPending ? { key: linkMomentKey, at: segmentStartSeconds } : undefined,
    [linkMomentPending, linkMomentKey, segmentStartSeconds]
  );
  const segmentIntentKey = segmentStartSeconds === undefined ? null : linkMomentKey;
  // A new opening with a moment shows the player it opens at.
  useEffect(() => {
    if (segmentIntentKey) setActiveTab('transcript');
  }, [segmentIntentKey]);
  // Where the listener is in which audio — a player brought back by the tabs
  // resumes there.
  const [listenerPosition, setListenerPosition] = useState<{
    audioUrl: string;
    at: number;
  } | null>(null);
  const [playedSegmentKey, setPlayedSegmentKey] = useState<string | null>(null);
  const [readyAudioUrl, setReadyAudioUrl] = useState<string | null>(null);

  const isMissedCall = record?.callCategory === 'missed';
  // While the analysis is still loading, fall back to the list-row score so
  // the badge paints with the header instead of popping in later. Both are the
  // canonical score of the current analysis (#1203), so the seed never flips
  // to a different number when the analysis arrives.
  const qualityScore = analysis?.result?.summary?.qualityScore ?? seedQualityScore;
  const saleProbability = analysis?.result?.summary?.saleProbability;
  const attentionReason = analysis?.result?.summary?.attentionReason;
  // Default for the incident form's severity field — the user can still
  // change it there. Unknown score (null) stays at the least severe default
  // rather than guessing.
  const defaultIncidentSeverity: 'low' | 'medium' | 'high' | 'critical' =
    qualityScore != null && qualityScore < 50
      ? 'critical'
      : qualityScore != null && qualityScore < 70
        ? 'high'
        : 'medium';

  // Deal-stage badge + "scored by" caption (Phase B, B-7). Resolved here
  // (not in AnalysisPanel) so the panel stays presentational: names come
  // from the cached CRM stages reference and prompt-profiles list, never
  // from the analysis/record's raw ids directly. Absent/stale ids or an
  // unavailable reference (embedded mode, no CRM connection) resolve to
  // `null` and AnalysisPanel/StageBadge hide the corresponding piece.
  const { promptProfileName, crmStageName, crmPipelineName, crmMultiplePipelines } =
    resolveStageBadgeData({
      crmPipelineId: record?.crmPipelineId,
      crmStageId: record?.crmStageId,
      promptProfileId: analysis?.promptProfileId,
      promptProfiles,
      crmStagesReference,
    });
  const typeLabel =
    record?.type === 'call'
      ? record?.callDirection === 'outbound'
        ? t('records.header.outgoingCall', 'Outgoing Call')
        : record?.callDirection === 'inbound'
          ? t('records.header.incomingCall', 'Incoming Call')
          : t('records.header.callRecord', 'Call')
      : t('records.header.chatRecord', 'Chat');
  // The agent name always stays first for a consistent visual order; the call
  // direction is encoded by the arrow glyph only. Outbound: agent \u2192 participant
  // (we called out). Inbound: agent \u2190 participant (they called us).
  const isInboundCall = record?.callDirection === 'inbound';
  const directionArrow = isInboundCall ? '\u2190' : '\u2192';

  const participantsLabel = participantsPlainLabel(record, directionArrow);
  const participantsDisplayLabel = participantsDetailedLabel(record, directionArrow);

  // Registered here, BELOW the header's own strings, because the host now
  // carries the name the assistant shows for this record (#922). Naming it
  // from anywhere else would let the panel and the page behind it disagree.
  // `assistantHostKind` still gates registration: while the record is unknown
  // there is no host, and the panel keeps the topic it had.
  // The date is part of the name, not decoration: an agent calling the same
  // customer twice produces two records whose type and participants read
  // identically, and without it the panel would look unchanged while it had
  // in fact switched to another record and another conversation (#922).
  const assistantLabel = [
    typeLabel,
    participantsLabel,
    formatDateTimeLocaleShort(record?.createdAt),
  ]
    .filter(Boolean)
    .join(' · ');
  // The deal (lead, contact, company) this record belongs to — what the
  // «ask about the deal» chip and the scope switcher are about (#765 Ф2).
  // Named with the link's own text, so the panel and the header agree.
  // `installationId` is converted explicitly: `undefined` would drop the key
  // from the thread and merge a Public API record's deal with a portal's.
  const assistantEntity: AssistantHostEntity | null =
    record?.crmEntityType && record.crmEntityId
      ? {
          entityType: record.crmEntityType as CrmEntityType,
          entityId: record.crmEntityId,
          installationId: record.installationId ?? null,
          // Localized, unlike the header link's own text (`formatCrmEntity` is
          // English-only): the panel says «Сделка: Ромашка», not «Deal:»
          // inside a Russian sentence (#1041 review).
          label: record.crmEntityName
            ? t(`assistant:entityLabel.named.${record.crmEntityType}`, {
                name: record.crmEntityName,
              })
            : t(`assistant:entityLabel.numbered.${record.crmEntityType}`, {
                id: record.crmEntityId,
              }),
        }
      : null;
  useAssistantHostBridge(
    {
      recordId,
      label: assistantLabel,
      recordType: assistantHostKind(record?.type),
      entity: assistantEntity,
      activeTab,
      setActiveTab,
      hasAudio: !!audioUrl,
      isAudioReady: !!audioUrl && readyAudioUrl === audioUrl,
      playSegment: handlePlayAudioSegment,
      seek: setCurrentTime,
      openRecord: onOpenAssistantRecord,
    },
    useAssistantHostRegistry()
  );
  /** Both header layouts open the same topic — one name, one place. */
  const assistantTopic = {
    subject: { scope: 'record' as const, recordId },
    label: assistantLabel,
  };

  const handleCreateTaskInB24 = async () => {
    if (!record) return;
    if (creatingTask) return;
    setCreatingTask(true);
    const taskTitle = `Follow-up: ${participantsLabel || record.id.slice(0, 8)}`;
    const taskDescription = [
      `Record: ${record.id}`,
      `Project: ${record.projectName || '—'}`,
      `Quality score: ${qualityScore != null ? formatQualityScore(qualityScore) : '—'}`,
      `Attention: ${attentionReason || t('records.actions.noCriticalFlags')}`,
      `Created at: ${formatDateTimeLocaleShort(record.createdAt) || '—'}`,
    ].join('\n');

    try {
      let copied = false;
      try {
        await navigator.clipboard.writeText(`${taskTitle}\n\n${taskDescription}`);
        copied = true;
      } catch {
        copied = false;
      }

      // The button only renders when Bitrix24 is connected (bitrix24Domain set),
      // so we can build the deep-link straight away.
      const url = new URL(
        `https://${bitrix24Domain}/company/personal/user/0/tasks/task/edit/0/`
      );
      url.searchParams.set('TITLE', taskTitle);
      url.searchParams.set('DESCRIPTION', taskDescription);
      window.open(url.toString(), '_blank', 'noopener,noreferrer');

      toast.success(
        t('records.actions.taskOpened', 'Task form opened in Bitrix24'),
        copied
          ? t('records.actions.taskCopiedToClipboard', 'Task details copied to clipboard')
          : t(
              'records.actions.fillTaskManually',
              'Fill task details manually in Bitrix24'
            )
      );
    } catch (err) {
      toast.error(
        t('records.actions.taskCreateFailed', 'Failed to open Bitrix24 task form'),
        err instanceof Error ? err.message : t('common:common.errorUnknown')
      );
    } finally {
      setCreatingTask(false);
    }
  };

  const handleOpenIncidentModal = () => {
    if (!record) return;
    if (!record.analysisId || !record.projectId) {
      toast.error(
        t('records.actions.incidentFormFailed', 'Failed to open incident form'),
        t(
          'records.actions.missingIncidentData',
          'Analysis or project context is missing for this record'
        )
      );
      return;
    }
    setShowIncidentModal(true);
  };

  // Writing is offered only over notes the reader can see (#2914): a refused
  // or still-loading list would let a Note through that the server refuses.
  // Reading is not writing (#2915): an agent reads a colleague's call by its
  // grant and writes only on its own — the list says which (`canAnnotate`,
  // never true before the list has loaded).
  const canAnnotate = annotationsWritable;
  const notesMessage =
    annotationsState === 'denied'
      ? t(
          'records.notes.unavailable',
          "You don't have access to the notes on this conversation."
        )
      : annotationsState === 'failed'
        ? t(
            'records.notes.loadFailed',
            "Couldn't load the notes. Open the conversation again to retry."
          )
        : annotationsState === 'loading'
          ? t('common:common.loading', 'Loading...')
          : canAnnotate
            ? t(
                'records.notes.empty',
                'No notes yet. Select text in the transcript to add annotations.'
              )
            : // No invitation to an action this reader is not offered (#2915).
              t('records.notes.emptyReadOnly', 'No notes yet.');

  const handleDeleteAnnotation = async (annotationId: string) => {
    if (!record?.analysisId) return;
    if (!confirm(t('annotations.deleteConfirm', 'Delete this annotation?'))) return;
    const analysisId = record.analysisId;
    try {
      await apiClient.analyses.deleteAnnotation(analysisId, annotationId);
      toast.success(t('annotations.deleted', 'Annotation deleted'));
      void queryClient.invalidateQueries({
        queryKey: ['analyses', 'annotations', analysisId],
      });
    } catch (err) {
      logError(
        'annotations.delete_failed',
        { analysisId, annotationId },
        err instanceof Error ? err : undefined
      );
      toast.error(t('annotations.deleteFailed', 'Failed to delete annotation'));
    }
  };

  // -----------------------------------------------------------------------
  // Loading state
  // -----------------------------------------------------------------------
  if (loading) {
    return (
      <div
        ref={setHeaderNode}
        className="flex flex-col h-full overflow-hidden bg-bg"
        data-testid="record-detail-inline-loading"
      >
        <div className="px-3 py-2 border-b border-border">
          <SkeletonCard showAvatar={false} lines={2} />
        </div>
        <div className="flex-1 p-4 space-y-4">
          <SkeletonMedia aspectRatio="16/9" />
          <SkeletonCard showAvatar={false} lines={6} />
        </div>
      </div>
    );
  }

  // -----------------------------------------------------------------------
  // Error state
  // -----------------------------------------------------------------------
  if (error && !loading) {
    return (
      <div
        className="flex flex-col h-full overflow-hidden bg-bg"
        data-testid="record-detail-inline-error"
      >
        <div className="flex-1 p-4">
          <div className="rounded-lg border border-danger bg-danger-dim p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="h-5 w-5 text-danger" />
              <h3 className="text-base font-semibold text-danger-fg">
                {t('records.failedToLoad', 'Failed to Load Record')}
              </h3>
            </div>
            <p className="text-sm text-fg-muted">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!record) return null;

  // Audio is expected for non-missed calls. On a seed record fileUri is not
  // known yet — assume audio so the slot is reserved from the first paint;
  // the real record corrects it within one round-trip if there is none.
  const expectAudio =
    record.type === 'call' && !isMissedCall && (record.fileUri ? true : isSeedRecord);
  // The skeleton is a promise that a player is coming. Show it only while
  // that is true; once the file is known not to be coming, say so instead of
  // animating forever.
  const showAudioPlaceholder = expectAudio && !audioUrl && audioState === 'preparing';
  const showAudioUnavailable = expectAudio && !audioUrl && audioState === 'unavailable';

  // Which of the company's own numbers the call went through. On a raw
  // telephony feed the operator is often unattributed, so the line is the one
  // thing that tells two outbound calls apart.
  const lineChip = record.lineNumber ? (
    <span className="text-fg-muted mr-2" data-testid="record-line">
      {t('records.lineLabel', { number: record.lineNumber })}
      {record.lineCarrier && ` · ${record.lineCarrier}`}
    </span>
  ) : null;

  // Recording length, and — when they diverge — how much of it was actually a
  // conversation. The two are different numbers on a call that spent its time
  // ringing or on hold, and the second is the one that was billed.
  const durationChip =
    record.durationSeconds == null ? null : (
      <span className="text-fg-muted mr-2">
        {formatDuration(record.durationSeconds)}
        {record.speechSeconds != null &&
          record.speechSeconds <= record.durationSeconds - SPEECH_GAP_VISIBLE_SECONDS && (
            <span className="ml-1" data-testid="record-speech-duration">
              {t('records.speechOfWhich', {
                duration: formatDuration(record.speechSeconds),
              })}
            </span>
          )}
      </span>
    );

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div
      ref={setHeaderNode}
      className="flex flex-col h-full overflow-hidden bg-bg"
      data-testid="record-detail-inline"
    >
      {/* ================================================================= */}
      {/* 1. COMPACT HEADER — matches prototype .detail-header              */}
      {/* padding: sp-2 sp-3 (8px 12px), border-bottom, bg-base            */}
      {/* ================================================================= */}
      <div className="shrink-0 border-b border-border bg-bg">
        {/* Row 1: Score badge + Title block */}
        <div className="flex items-start gap-3 px-3 py-2">
          {/* Score badge — 40x40, rounded-lg, colored */}
          {qualityScore != null && (
            <div
              className={cn(
                'flex items-center justify-center min-w-10 h-10 px-1 rounded-lg text-lg font-bold shrink-0 mt-0.5',
                getScoreStyle(qualityScore)
              )}
            >
              {formatQualityScore(qualityScore)}
            </div>
          )}

          {/* Title block */}
          <div className="min-w-0 flex-1">
            {/* Title line: on wide panels stars sit here at right; on narrow — full width */}
            <div className="flex items-center gap-2 text-sm font-medium text-fg">
              {record.type === 'call' ? (
                record.callDirection === 'inbound' ? (
                  <PhoneIncoming className="h-3.5 w-3.5 shrink-0 text-info" />
                ) : record.callDirection === 'outbound' ? (
                  <PhoneOutgoing className="h-3.5 w-3.5 shrink-0 text-success" />
                ) : (
                  <Phone className="h-3.5 w-3.5 shrink-0 text-primary" />
                )
              ) : (
                <MessageSquare className="h-3.5 w-3.5 shrink-0 text-primary" />
              )}
              <span className="shrink-0">{typeLabel}</span>
              {participantsDisplayLabel && (
                <>
                  <span className="text-fg-faint shrink-0">&middot;</span>
                  <span className="font-normal truncate min-w-0">
                    {participantsDisplayLabel}
                  </span>
                </>
              )}
              {/* Stars inline (only on wide panels) + arrows always on right */}
              <div className="shrink-0 ml-auto flex items-center pl-1">
                {!isStarsBelow && (
                  <AgentRating
                    recordId={recordId}
                    initialRating={record?.agentRating}
                    initialFeedback={record?.agentRatingText}
                    raterName={record?.agentRatingUserName}
                    apiClient={apiClient}
                    size="sm"
                  />
                )}
                {(hasPrev || hasNext) && (
                  <div className="flex items-center ml-1">
                    <button
                      onClick={hasPrev ? onNavigatePrev : undefined}
                      disabled={!hasPrev}
                      aria-label={t('records.navigation.prev', 'Previous record')}
                      className="w-6 h-6 flex items-center justify-center text-fg-muted hover:text-fg disabled:opacity-25 disabled:pointer-events-none transition-colors focus-visible:outline-none"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                      onClick={hasNext ? onNavigateNext : undefined}
                      disabled={!hasNext}
                      aria-label={t('records.navigation.next', 'Next record')}
                      className="w-6 h-6 flex items-center justify-center text-fg-muted hover:text-fg disabled:opacity-25 disabled:pointer-events-none transition-colors focus-visible:outline-none"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Second row: stars below title on medium/narrow panels */}
            {isStarsBelow && (
              <div className="mt-1">
                <AgentRating
                  recordId={recordId}
                  initialRating={record?.agentRating}
                  initialFeedback={record?.agentRatingText}
                  raterName={record?.agentRatingUserName}
                  apiClient={apiClient}
                  size="sm"
                />
              </div>
            )}

            {/* Meta chips — 1 row on wide panels (≥900px), 2 rows on narrow */}
            {isTabsMode ? (
              <div className="flex flex-col gap-y-0.5 text-xs mt-0.5">
                {/* Row 1: duration, category, CRM link, series, project/team */}
                <div className="flex flex-wrap items-center gap-y-0.5">
                  {durationChip}
                  {lineChip}
                  {/* Category tag — same as the list row, so the header itself
                      explains e.g. why an "Other" call has no score */}
                  {record.callCategory && (
                    <span
                      className={cn(
                        'inline-flex items-center rounded px-1.5 py-0.5 text-micro font-medium mr-2',
                        getCategoryTagStyle(record.callCategory)
                      )}
                    >
                      {t(
                        `records.${callCategoryLabelKey(record.callCategory, record.type, record.callDirection)}`,
                        record.callCategory
                      )}
                    </span>
                  )}
                  {record.dealOutcome && (
                    <DealOutcomeBadge outcome={record.dealOutcome} className="mr-2" />
                  )}
                  {record.crmEntityType && record.crmEntityId && (
                    <span className="inline-flex items-center gap-1 mr-2">
                      <span className="text-fg-faint">&middot;</span>
                      <CrmEntityLink
                        entityType={record.crmEntityType as CrmEntityType}
                        entityId={record.crmEntityId}
                        entityName={record.crmEntityName}
                        vendor={record.crmVendor}
                        domain={record.crmDomain}
                        className="text-xs font-medium"
                      />
                      <AssistantEntityChip className="ml-1" />
                    </span>
                  )}
                  {record.callSeriesId && record.callSeriesNumber && (
                    <span className="inline-flex items-center gap-1 mr-2">
                      <span className="text-fg-faint">&middot;</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSeriesClick?.(record.callSeriesId!)}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary transition-colors"
                      >
                        <Link2 className="h-3.5 w-3.5" />
                        <span>Call #{record.callSeriesNumber}</span>
                      </Button>
                    </span>
                  )}
                  {record.projectName && (
                    <span className="inline-flex items-center gap-1 mr-2">
                      <span className="text-fg-faint">&middot;</span>
                      {record.projectId ? (
                        <Link
                          to="/projects/$projectId/settings"
                          params={{ projectId: record.projectId }}
                          className="text-fg-muted hover:text-primary transition-colors"
                        >
                          {record.projectName}
                        </Link>
                      ) : (
                        <span className="text-fg-muted">{record.projectName}</span>
                      )}
                    </span>
                  )}
                </div>
                {/* Row 2: отметки времени + вероятность сделки.
                    Внешнее условие обязано включать `versionTs`: у записи с
                    известным временем разговора, но без `created_at`, строка
                    иначе не рисуется вовсе — и в узкой раскладке отметок нет,
                    хотя в широкой они есть. */}
                {(record.createdAt || record.versionTs || saleProbability != null) && (
                  <div className="flex flex-wrap items-center gap-y-0.5">
                    {(record.createdAt || record.versionTs) && (
                      <RecordTimestamps
                        type={record.type}
                        eventTime={record.versionTs}
                        createdAt={record.createdAt}
                        durationSeconds={record.durationSeconds}
                        className="text-fg-muted mr-2"
                      />
                    )}
                    <SaleProbabilityBadge probability={saleProbability} />
                  </div>
                )}
              </div>
            ) : (
              /* Wide panel (≥900px): all chips in one row */
              <div className="flex flex-wrap items-center gap-y-0.5 text-xs mt-0.5">
                {durationChip}
                {lineChip}
                {/* Category tag — same as the list row, so the header itself
                    explains e.g. why an "Other" call has no score */}
                {record.callCategory && (
                  <span
                    className={cn(
                      'inline-flex items-center rounded px-1.5 py-0.5 text-micro font-medium mr-2',
                      getCategoryTagStyle(record.callCategory)
                    )}
                  >
                    {t(
                      `records.${callCategoryLabelKey(record.callCategory, record.type, record.callDirection)}`,
                      record.callCategory
                    )}
                  </span>
                )}
                {record.dealOutcome && (
                  <DealOutcomeBadge outcome={record.dealOutcome} className="mr-2" />
                )}
                {record.crmEntityType && record.crmEntityId && (
                  <span className="inline-flex items-center gap-1 mr-2">
                    <span className="text-fg-faint">&middot;</span>
                    <CrmEntityLink
                      entityType={record.crmEntityType as CrmEntityType}
                      entityId={record.crmEntityId}
                      entityName={record.crmEntityName}
                      vendor={record.crmVendor}
                      domain={record.crmDomain}
                      className="text-xs font-medium"
                    />
                    <AssistantEntityChip className="ml-1" />
                  </span>
                )}
                {record.callSeriesId && record.callSeriesNumber && (
                  <span className="inline-flex items-center gap-1 mr-2">
                    <span className="text-fg-faint">&middot;</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onSeriesClick?.(record.callSeriesId!)}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary transition-colors"
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      <span>Call #{record.callSeriesNumber}</span>
                    </Button>
                  </span>
                )}
                {record.projectName && (
                  <span className="inline-flex items-center gap-1 mr-2">
                    <span className="text-fg-faint">&middot;</span>
                    {record.projectId ? (
                      <Link
                        to="/projects/$projectId/settings"
                        params={{ projectId: record.projectId }}
                        className="text-fg-muted hover:text-primary transition-colors"
                      >
                        {record.projectName}
                      </Link>
                    ) : (
                      <span className="text-fg-muted">{record.projectName}</span>
                    )}
                  </span>
                )}
                {(record.createdAt || record.versionTs) && (
                  <span className="inline-flex items-center gap-1 mr-2">
                    <span className="text-fg-faint">&middot;</span>
                    <RecordTimestamps
                      type={record.type}
                      eventTime={record.versionTs}
                      createdAt={record.createdAt}
                      durationSeconds={record.durationSeconds}
                      className="text-fg-muted"
                    />
                  </span>
                )}
                <SaleProbabilityBadge probability={saleProbability} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* A failed run is explained ONCE, above tabs and columns: in tabs mode the
          score column hides behind its tab, and a record whose transcript or
          analysis exists (delivery_failed, analysis_failed) never reaches the
          panels' fallbacks — the explanation has to stand where it is seen in
          both layouts (#1096). */}
      {isFailedRecordStatus(record.status) && (
        <div className="px-3 pt-3 shrink-0">
          <RecordFailedNotice
            status={record.status}
            canRetry={canRetryFailed}
            hasRetrySource={hasRetrySource}
            actionKnown={retryAnswerKnown}
            retryOutcome={record.retryOutcome}
            onRetry={handleRetryFailed}
            isDemo={record.isDemo}
            // One run at a time: a re-analysis in flight keeps the status, and a
            // retry on top of it would start a second paid run.
            isRetrying={isRetryingFailed || reanalyzing}
          />
        </div>
      )}

      {isRestartPending && (
        <div className="px-3 pt-3 shrink-0">
          <RecordHaltedNotice
            status={record.status}
            errorMessage={record.errorMessage}
            canStart={canStart}
            isDemo={record.isDemo}
            onStart={onStart}
            isStarting={isStarting}
            recordType={record.type}
            hasEarlierResults
          />
        </div>
      )}

      {/* ================================================================= */}
      {/* 2. TABS — border-bottom, bg-bg, padding 0 sp-3                   */}
      {/* ================================================================= */}
      <div className="flex border-b border-border bg-bg px-3 shrink-0">
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'px-4 py-3 text-sm border-b-2 transition-colors',
            activeTab === 'transcript'
              ? 'text-primary border-primary'
              : 'text-fg-muted border-transparent hover:text-fg'
          )}
          onClick={() => setActiveTab('transcript')}
        >
          {t('records.tabs.transcript', 'Transcript')}
        </Button>
        {/* Score tab — only in tabs mode (replaces side-by-side right column) */}
        {isTabsMode && (
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              'px-4 py-3 text-sm border-b-2 transition-colors',
              activeTab === 'score'
                ? 'text-primary border-primary'
                : 'text-fg-muted border-transparent hover:text-fg'
            )}
            onClick={() => setActiveTab('score')}
          >
            {t('records.tabs.score', 'Score')}
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            'px-4 py-3 text-sm border-b-2 transition-colors',
            activeTab === 'notes'
              ? 'text-primary border-primary'
              : 'text-fg-muted border-transparent hover:text-fg'
          )}
          onClick={() => setActiveTab('notes')}
        >
          {t('records.tabs.notes', 'Notes')}
          {annotationsState === 'ready' && (
            <>
              {' '}
              <span className="text-fg-faint text-micro">({annotations.length})</span>
            </>
          )}
        </Button>
      </div>

      {/* ================================================================= */}
      {/* 3. BODY — 2-column grid (50/50 split)                             */}
      {/* ================================================================= */}
      {/* 'fast' (150ms): the entrance animation plays on EVERY record switch
          (keyed by record.id) on top of network latency — keep it short. */}
      <AnimatedContainer
        key={record.id}
        direction="up"
        duration="fast"
        className={cn(
          'flex-1 min-h-0 overflow-hidden',
          isTabsMode ? 'grid grid-cols-1' : 'grid grid-cols-2'
        )}
      >
        {/* LEFT COLUMN — Transcript / Notes panel (hidden when score tab active in tabs mode) */}
        {(!isTabsMode || activeTab !== 'score') && (
          <div
            className={cn(
              'col-span-1 overflow-y-auto',
              !isTabsMode && 'border-r border-border'
            )}
          >
            {activeTab === 'transcript' ? (
              <>
                {/* Sticky: AudioPlayer + SpeakerTalkRatio (collapsible) */}
                <div className="sticky top-0 z-10 bg-bg-elevated border-b border-border p-3">
                  {record.type === 'call' && audioUrl && (
                    <>
                      <AudioPlayer
                        ref={audioPlayerRef}
                        audioUrl={audioUrl}
                        durationSeconds={record.durationSeconds ?? undefined}
                        onTimeUpdate={(at) => {
                          setCurrentTime(at);
                          setListenerPosition({ audioUrl, at });
                        }}
                        onDurationLoaded={() => setReadyAudioUrl(audioUrl)}
                        linkMoment={linkMoment}
                        onLinkMomentApplied={setSpentLinkMomentKey}
                        resumeAtSeconds={
                          listenerPosition?.audioUrl === audioUrl
                            ? listenerPosition.at
                            : undefined
                        }
                        compact={playerCompact}
                      />
                      {segmentIntentKey &&
                        playedSegmentKey !== segmentIntentKey &&
                        segmentStartSeconds !== undefined && (
                          <div
                            data-testid="evidence-clip-prompt"
                            className="mt-2 flex items-center justify-between gap-3 rounded-card border border-primary/30 bg-primary-dim px-3 py-2"
                          >
                            <span className="text-xs text-fg-secondary">
                              {t(
                                'records.evidenceClip.ready',
                                'Evidence at {{timecode}} · {{duration}} seconds',
                                {
                                  timecode: formatDuration(segmentStartSeconds),
                                  duration: segmentDurationSeconds,
                                }
                              )}
                            </span>
                            <Button
                              size="sm"
                              disabled={readyAudioUrl !== audioUrl}
                              onClick={() => {
                                handlePlayAudioSegment(
                                  segmentStartSeconds,
                                  segmentDurationSeconds
                                );
                                setPlayedSegmentKey(segmentIntentKey);
                              }}
                            >
                              {t('records.evidenceClip.play', 'Play fragment')}
                            </Button>
                          </div>
                        )}
                    </>
                  )}
                  {/* Same-size stand-in while the blob downloads — the player
                      mounting later must not shift the transcript below. */}
                  {showAudioPlaceholder && (
                    <AudioPlayerSkeleton compact={playerCompact} />
                  )}
                  {showAudioUnavailable && (
                    <p
                      className="text-sm text-fg-muted py-2"
                      data-testid="audio-unavailable"
                    >
                      {t('records.audio.unavailable')}
                    </p>
                  )}
                  {!playerCompact &&
                    transcript?.diarization &&
                    transcript.diarization.length > 0 && (
                      <SpeakerTalkRatio
                        segments={transcript.diarization}
                        speakerRoles={analysis?.result?.speakerRoles}
                        agentId={record.agentId}
                        agentName={record?.agentName || undefined}
                        participantName={record.participantName}
                        className="mt-3"
                      />
                    )}
                  {/* Toggle compact/expanded */}
                  {record.type === 'call' && (audioUrl || showAudioPlaceholder) && (
                    // Collapse toggle belongs to the player and its stand-in
                    // only — there is nothing to collapse under a plain
                    // "unavailable" line.
                    <button
                      onClick={() =>
                        setPlayerExpanded((prev) =>
                          prev === null ? !isSmallScreen : !prev
                        )
                      }
                      className="flex items-center justify-center w-full mt-1.5 py-0.5 text-fg-muted hover:text-fg transition-colors"
                      aria-label={playerCompact ? 'Expand player' : 'Collapse player'}
                    >
                      {playerCompact ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronUp className="h-3.5 w-3.5" />
                      )}
                    </button>
                  )}
                </div>

                {/* Missed call banner */}
                {isMissedCall && (
                  <div className="px-3 pt-3">
                    <div className="flex flex-col items-center py-8 text-center">
                      <PhoneMissed className="h-8 w-8 text-fg-muted mx-auto mb-2" />
                      <p className="text-sm text-fg-muted">
                        {t(
                          'records.missedCall.noTranscript',
                          'Missed call — transcript not available'
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {/* Transcript */}
                {!isMissedCall && (
                  <div className="p-3">
                    {transcriptLoading && !transcript ? (
                      <SkeletonCard showAvatar={false} lines={8} />
                    ) : transcript ? (
                      <>
                        {/* Copy transcript button */}
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-base font-semibold text-fg">
                            {t('records.transcript', 'Transcript')}
                          </h3>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              // Копия уходит тому же читателю, что смотрит на
                              // панель: маркер вложения в ней должен стоять
                              // теми же словами, а не машинным токеном (#476).
                              // И звуки колебания скрыты так же, как на панели (#819).
                              // Подпись — та же, что на панели: имя, а не id (#889).
                              const speakerNames = speakerNamesFor(
                                transcript.diarization.map((seg) => seg.speaker),
                                {
                                  speakerRoles: analysis?.result?.speakerRoles,
                                  agentId: record.agentId,
                                  agentName: record.agentName,
                                  participantName: record.participantName,
                                }
                              );
                              const fullText = transcript.diarization
                                .map(
                                  (seg) =>
                                    `[${speakerLabel(seg.speaker, speakerNames, t)}] ${renderTranscriptText(
                                      seg.text,
                                      i18n.language,
                                      transcriptDisplayOptions(record.type)
                                    )}`
                                )
                                .join('\n\n');
                              navigator.clipboard
                                .writeText(fullText)
                                .then(() => {
                                  toast.success(
                                    t('records.copyTranscript.copied'),
                                    t('records.copyTranscript.copiedMessage')
                                  );
                                })
                                .catch(() => {
                                  toast.error(
                                    t('records.copyTranscript.copyFailed'),
                                    t('records.copyTranscript.copyFailedMessage')
                                  );
                                });
                            }}
                            className="flex items-center gap-1.5 rounded-button border border-border bg-bg-elevated px-2.5 py-1.5 text-xs font-medium text-fg transition-all duration-150 hover:bg-bg-elevated-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            title={t('records.copyTranscript.title', 'Copy transcript')}
                          >
                            <Copy className="h-3 w-3" />
                            {t('records.copyTranscript.button', 'Copy')}
                          </Button>
                        </div>
                        <TranscriptViewer
                          segments={transcript.diarization}
                          speakerRoles={analysis?.result?.speakerRoles}
                          currentTime={currentTime}
                          onSeek={handleSeekAudio}
                          analysisId={record.analysisId || undefined}
                          annotations={annotations}
                          onAnnotationCreate={
                            canAnnotate ? handleAnnotationCreate : undefined
                          }
                          stripHesitations={hidesHesitationSounds(record.type)}
                          agentId={record.agentId}
                          agentName={record.agentName}
                          participantName={record.participantName}
                          highlightQuote={highlightQuote}
                        />
                      </>
                    ) : record.callCategory === 'short' ? (
                      <ShortCallBanner
                        transcribed={false}
                        durationSeconds={record.durationSeconds ?? undefined}
                      />
                    ) : isHaltedRecordStatus(record.status) && !isRestartPending ? (
                      // Not a delay but a decision — "may still be in progress"
                      // would send the reader off to wait for nothing.
                      <RecordHaltedNotice
                        status={record.status}
                        errorMessage={record.errorMessage}
                        canStart={canStart}
                        isDemo={record.isDemo}
                        onStart={onStart}
                        isStarting={isStarting}
                        onResume={handleResumeHalted}
                        isResuming={isResumingHalted}
                        recordType={record?.type}
                      />
                    ) : // A failure, not a delay: the notice above the tabs explains
                    // it. «May still be in progress» here told the customer to
                    // wait for a run that had stopped (#1096).
                    isFailedRecordStatus(record.status) || isRestartPending ? null : (
                      <div className="text-center py-8 text-fg-muted text-xs">
                        {t(
                          'records.noTranscript',
                          'Transcript not available yet. Processing may still be in progress.'
                        )}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* Notes tab content */
              <div className="p-3">
                {annotations.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    {annotations.map((ann) => (
                      <div
                        key={ann.id}
                        className="rounded-sm border border-border bg-bg-elevated p-2 text-xs"
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2">
                            <span
                              className={cn(
                                'inline-flex items-center rounded-sm px-1.5 py-px text-2xs font-medium',
                                ann.severity === 'high'
                                  ? 'bg-danger-dim text-danger-fg'
                                  : ann.severity === 'medium'
                                    ? 'bg-warning-dim text-warning-fg'
                                    : 'bg-bg-elevated text-fg-muted'
                              )}
                            >
                              {ann.type.replace(/_/g, ' ')}
                            </span>
                            <span className="text-fg-faint">
                              {formatDateLocale(ann.createdAt)}
                            </span>
                          </div>
                          {/* Only the author edits or deletes a note (#2915). */}
                          {ann.canEdit && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setEditAnnotation(ann)}
                                className="p-1 text-fg-muted hover:text-fg transition-colors"
                                title={t('common:common.edit', 'Edit')}
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => void handleDeleteAnnotation(ann.id)}
                                className="p-1 text-fg-muted hover:text-danger transition-colors"
                                title={t('common:common.delete', 'Delete')}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <p className="text-fg-muted">{ann.note}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-8 text-center text-fg-muted text-xs">
                    <p>{notesMessage}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* RIGHT COLUMN — Analysis panel + Script Adherence */}
        {/* In tabs mode shown only when score tab is active; in 2-col mode always visible */}
        {(!isTabsMode || activeTab === 'score') && (
          <div className="col-span-1 overflow-y-auto p-3 pb-chat-safe space-y-4">
            {/* «Demo analysis» only over an analysis: a demo missed, short or
                waiting call has none (#1503). */}
            {record.isDemo && analysis && <DemoAnalysisNotice />}
            {analysisLoading && !analysis ? (
              <SkeletonCard showAvatar={false} lines={8} />
            ) : analysis ? (
              <AnalysisPanel
                result={analysis.result}
                componentScores={analysis.scores}
                voiceMetrics={analysis.voiceMetrics}
                verification={analysis.verification}
                scoredWithEarlierWeights={analysis.scoredWithEarlierWeights}
                qualityWeightShares={analysis.qualityWeightShares}
                promptProfileName={promptProfileName}
                crmStageName={crmStageName}
                crmPipelineName={crmPipelineName}
                crmMultiplePipelines={crmMultiplePipelines}
                crmStageProgression={record?.crmStageProgression}
                crmProgressionEvaluatedAt={record?.crmProgressionEvaluatedAt}
                diarization={transcript?.diarization}
                agentName={record?.agentName || undefined}
                scriptAdherence={scriptAdherence}
                scriptAdherenceLoading={scriptAdherenceLoading}
                criteria={criteria}
                criteriaLoading={criteriaLoading}
                criteriaFailed={criteriaFailed}
                builtinSources={builtinSources}
                builtinPausedByAelo={builtinPausedByAelo}
                callCategory={record.callCategory}
                durationSeconds={record.durationSeconds ?? undefined}
                onPlaySegment={handlePlayAudioSegment}
                agentReviewSlot={
                  // The review sits behind the same gate as the annotations
                  // (`gateAnalysisPanelRead`): shown only once that gate is
                  // known not to refuse this reader (#2914).
                  analysis.id &&
                  (annotationsState === 'ready' || annotationsState === 'failed') ? (
                    <AgentReviewSection
                      analysisId={analysis.id}
                      recordId={record.id}
                      projectId={record.projectId}
                      apiClient={apiClient}
                      userRole={userRole}
                    />
                  ) : undefined
                }
              />
            ) : record.callCategory === 'short' ? (
              // Too short to transcribe at all (<~15s): neither transcript nor
              // analysis — explain the reason instead of "still processing".
              <ShortCallBanner
                transcribed={false}
                durationSeconds={record.durationSeconds ?? undefined}
              />
            ) : record.isShortCall ? (
              <ShortCallBanner
                // The banner explains the gate, so it must show the number the gate
                // used — the conversation, falling back to the recording when it
                // was never measured. Showing the recording made the panel argue
                // with itself: "1:20" in the header, "too short" underneath.
                durationSeconds={
                  record.speechSeconds ?? record.durationSeconds ?? undefined
                }
                minDurationSeconds={
                  record.minCallDurationSeconds ?? DEFAULT_MIN_CALL_DURATION_SECONDS
                }
                variant={record.type === 'chat' ? 'chat' : 'call'}
              />
            ) : accessDenied ? (
              // The contents were refused, not missing. Reporting this as
              // "processing may still be in progress" sent a supervisor with no
              // project assignment chasing the pipeline for a permissions
              // problem — for weeks, in production.
              <div
                className="p-3 text-xs text-warning"
                data-testid="record-detail-access-denied"
              >
                {t(
                  'records.accessDeniedProject',
                  'You do not have access to this record’s project. Ask an administrator to assign it to you.'
                )}
              </div>
            ) : isHaltedRecordStatus(record.status) && !isRestartPending ? (
              <RecordHaltedNotice
                status={record.status}
                errorMessage={record.errorMessage}
                canStart={canStart}
                isDemo={record.isDemo}
                onStart={onStart}
                isStarting={isStarting}
                onResume={handleResumeHalted}
                isResuming={isResumingHalted}
                recordType={record?.type}
                className="m-3"
              />
            ) : isFailedRecordStatus(record.status) ||
              isRestartPending ? null : !isMissedCall ? ( // Explained once, above the tabs (#1096, #1264).
              <div className="p-3 text-xs text-fg-muted">
                {t(
                  'records.noAnalysis',
                  'Analysis not available yet. Processing may still be in progress.'
                )}
              </div>
            ) : null}
          </div>
        )}
      </AnimatedContainer>

      {/* ================================================================= */}
      {/* 4. ACTION BAR — padding sp-2 sp-3 (8px 12px), compact            */}
      {/* ================================================================= */}
      <div
        className={cn(
          'px-3 py-2 border-t border-border bg-bg shrink-0',
          isActionBarNarrow ? 'flex flex-col gap-1.5' : 'flex items-center gap-1.5'
        )}
      >
        {isActionBarNarrow ? (
          <>
            {/* Narrow: Row 1 — min-w-0 lets a button shrink below its label's natural
                width instead of forcing the row wider than its container now that
                Button's base classes are whitespace-nowrap (no more wrap-to-fit). */}
            <div className="flex items-center gap-1.5">
              {bitrix24Domain && (
                <Button
                  variant="default"
                  size="sm"
                  className="text-sm flex-1 min-w-0 justify-center"
                  onClick={() => void handleCreateTaskInB24()}
                  isLoading={creatingTask}
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  {t('records.actions.createTaskB24', 'Task in B24')}
                </Button>
              )}
              {canCreateIncident && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-sm flex-1 min-w-0 justify-center"
                  onClick={handleOpenIncidentModal}
                >
                  <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                  {t('records.actions.createIncident', 'Create incident')}
                </Button>
              )}
              {canAskAssistant && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-sm flex-1 min-w-0 justify-center text-ai"
                  onClick={() => assistantDrawer?.openAssistant(assistantTopic)}
                  data-testid="record-ask-assistant"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1" />
                  {t('records.actions.askAssistant', 'Ask the assistant')}
                </Button>
              )}
              {canAnnotate && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-sm flex-1 min-w-0 justify-center"
                  onClick={() => setShowAnnotationModal(true)}
                >
                  <StickyNote className="h-3.5 w-3.5 mr-1" />
                  {t('records.actions.note', 'Note')}
                </Button>
              )}
            </div>
            {/* Narrow: Row 2 */}
            <div className="flex items-center gap-1.5">
              {canReanalyze && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-sm flex-1 min-w-0 justify-center"
                  onClick={() => setShowReanalyze(!showReanalyze)}
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  {record.analysisId
                    ? t('records.actions.reanalyze', 'Re-analyze')
                    : t('records.actions.analyze', 'Analyze')}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                className="text-sm flex-1 min-w-0 justify-center"
                onClick={() =>
                  handleExportPdf({
                    promptProfileName,
                    crmStageName,
                    crmPipelineName,
                    crmMultiplePipelines,
                  })
                }
                isLoading={exportingPdf}
              >
                <Download className="h-3.5 w-3.5 mr-1" />
                {t('records.actions.export', 'Export')}
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Wide: single row. Per-button max-w-[190px] (not a shared container cap) —
                measured against real i18n strings: ru "Переанализировать" needs ~180px incl.
                icon+padding, so 190px covers every locale's longest label with margin. A
                per-button cap keeps the 2-3-button case sized like before (buttons don't
                stretch to fill the row when fewer show); a shared container total would
                have divided evenly by however many buttons render, ballooning short labels
                like "Note"/"Export" whenever the long ones are absent. */}
            {bitrix24Domain && (
              <Button
                variant="default"
                size="sm"
                className="text-sm flex-1 min-w-0 max-w-[190px] justify-center"
                onClick={() => void handleCreateTaskInB24()}
                isLoading={creatingTask}
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t('records.actions.createTaskB24', 'Task in B24')}
              </Button>
            )}
            {canCreateIncident && (
              <Button
                variant="outline"
                size="sm"
                className="text-sm flex-1 min-w-0 max-w-[190px] justify-center"
                onClick={handleOpenIncidentModal}
              >
                <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                {t('records.actions.createIncident', 'Create incident')}
              </Button>
            )}
            {canAskAssistant && (
              <Button
                variant="outline"
                size="sm"
                className="text-sm flex-1 min-w-0 max-w-[190px] justify-center text-ai"
                onClick={() => assistantDrawer?.openAssistant(assistantTopic)}
                data-testid="record-ask-assistant"
              >
                <Sparkles className="h-3.5 w-3.5 mr-1" />
                {t('records.actions.askAssistant', 'Ask the assistant')}
              </Button>
            )}
            {canAnnotate && (
              <Button
                variant="outline"
                size="sm"
                className="text-sm flex-1 min-w-0 max-w-[190px] justify-center"
                onClick={() => setShowAnnotationModal(true)}
              >
                <StickyNote className="h-3.5 w-3.5 mr-1" />
                {t('records.actions.note', 'Note')}
              </Button>
            )}
            {canReanalyze && (
              <Button
                variant="outline"
                size="sm"
                className="text-sm flex-1 min-w-0 max-w-[190px] justify-center"
                onClick={() => setShowReanalyze(!showReanalyze)}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                {record.analysisId
                  ? t('records.actions.reanalyze', 'Re-analyze')
                  : t('records.actions.analyze', 'Analyze')}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="text-sm flex-1 min-w-0 max-w-[190px] justify-center"
              onClick={() =>
                handleExportPdf({
                  promptProfileName,
                  crmStageName,
                  crmPipelineName,
                  crmMultiplePipelines,
                })
              }
              isLoading={exportingPdf}
            >
              <Download className="h-3.5 w-3.5 mr-1" />
              {t('records.actions.export', 'Export')}
            </Button>
          </>
        )}
      </div>

      {/* Re-analyze panel */}
      {showReanalyze && (
        <div className="mt-3 p-3 rounded-card border border-border bg-bg-surface">
          <div className="flex items-center gap-3">
            <div className="flex-1 min-w-0">
              {profilesLoading ? (
                <div className="flex items-center gap-2 text-sm text-fg-muted">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t('records.reanalyze.loadingProfiles', 'Loading profiles...')}
                </div>
              ) : promptProfiles.length > 0 ? (
                <Select
                  value={selectedPromptProfile}
                  onValueChange={(v) =>
                    setSelectedPromptProfile(typeof v === 'string' ? v : v[0] || '')
                  }
                  options={[
                    // 'auto' → no override: the backend resolves the bound
                    // profile for the record's category (the normal pipeline
                    // path). Radix Select forbids '' as an item value.
                    {
                      value: 'auto',
                      label: t(
                        'records.reanalyze.autoProfile',
                        'Auto — category profile'
                      ),
                    },
                    ...promptProfiles.map((p) => ({
                      value: p.id,
                      label: p.name,
                    })),
                  ]}
                  className="w-full"
                />
              ) : (
                <span className="text-sm text-fg-muted">
                  {t('records.reanalyze.defaultProfile', 'Default profile')}
                </span>
              )}
            </div>
            <Button
              size="sm"
              onClick={handleReanalyze}
              isLoading={reanalyzing}
              // The mirror of the failure notice's guard: a retry being sent
              // still reads the old failed status, and a re-analysis started
              // on top of it would be a second paid run of the same record.
              disabled={reanalyzing || isRetryingFailed}
            >
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              {t('records.reanalyze.start', 'Start')}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowReanalyze(false)}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          {(reanalyzeStatus === 'queued' || reanalyzeStatus === 'analyzing') && (
            <div className="mt-2 flex items-center gap-2 text-sm text-fg-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {t('records.reanalyze.inProgress', 'Re-analyzing...')}
              {elapsedSeconds > 0 && (
                <span>({formatDurationUnit(elapsedSeconds, 'second')})</span>
              )}
            </div>
          )}
          {reanalyzeStatus === 'failed' && reanalyzeError && (
            <div className="mt-2 text-sm text-danger">{reanalyzeError}</div>
          )}
        </div>
      )}

      {/* ================================================================= */}
      {/* MODALS                                                            */}
      {/* ================================================================= */}

      {/* Annotation Modal — apiClient is passed explicitly (the same one this
          panel already resolved for embedded vs. standalone use), same
          precedent as IncidentModal below, so the Note button works in
          embedded mode too instead of silently no-oping. */}
      {record.analysisId && (
        <AnnotationModal
          analysisId={record.analysisId}
          recordId={record.id}
          isOpen={showAnnotationModal || editAnnotation !== null}
          onClose={() => {
            setShowAnnotationModal(false);
            setEditAnnotation(null);
          }}
          onSuccess={handleAnnotationSuccess}
          selectedText={annotationSelectedText}
          startPosition={
            editAnnotation
              ? (editAnnotation.startPosition ?? undefined)
              : annotationStartPos
          }
          endPosition={
            editAnnotation ? (editAnnotation.endPosition ?? undefined) : annotationEndPos
          }
          mode={editAnnotation ? 'edit' : 'create'}
          annotation={editAnnotation ?? undefined}
          apiClient={apiClient}
        />
      )}

      {/* Incident Modal — apiClient is passed explicitly (the same one this
          panel already resolved for embedded vs. standalone use). */}
      {record.analysisId && record.projectId && (
        <IncidentModal
          analysisId={record.analysisId}
          recordId={record.id}
          projectId={record.projectId}
          isOpen={showIncidentModal}
          onClose={() => setShowIncidentModal(false)}
          onSuccess={() => {
            // Refresh the Incidents tab so the new entry shows up there.
            void queryClient.invalidateQueries({ queryKey: ['incidents'] });
          }}
          initialSeverity={defaultIncidentSeverity}
          apiClient={apiClient}
        />
      )}
    </div>
  );
}
